<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import { useCetakersFlow } from 'dashboard/composables/useCetakersFlow';

const { uiSettings, updateUISettings } = useUISettings();
const { isCetakersFlowEnabled } = useCetakersFlow();

const shouldShowPanel = computed(() => {
  return isCetakersFlowEnabled.value && uiSettings.value?.is_cetakers_flow_panel_open;
});

const closePanel = () => {
  updateUISettings({ is_cetakers_flow_panel_open: false });
};
</script>

<template>
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="() => closePanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <!-- Header -->
    <header class="flex items-center justify-between p-4 border-b border-n-weak bg-n-surface-1">
      <div class="flex items-center gap-2">
        <div class="i-lucide-printer text-n-slate-11 size-5"></div>
        <h3 class="text-sm font-semibold text-n-slate-12 m-0">Cetakers Flow</h3>
      </div>
      <Button
        ghost
        sm
        icon="i-lucide-x"
        class="text-n-slate-10 hover:text-n-slate-12"
        @click="closePanel"
      />
    </header>

    <!-- Search Bar -->
    <div class="p-3 border-b border-n-weak bg-n-surface-2">
      <div class="relative flex items-center w-full">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span class="i-lucide-search text-n-slate-10 size-4"></span>
        </div>
        <input
          type="text"
          placeholder="Cari SPK, Invoice, atau Produk..."
          class="block w-full pl-9 pr-3 py-2 text-sm border border-n-weak rounded-md leading-5 bg-n-surface-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
        />
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex items-center border-b border-n-weak bg-n-surface-1 px-2">
      <button class="flex-1 py-3 text-xs font-medium text-n-brand border-b-2 border-n-brand focus:outline-none">
        Invoices
      </button>
      <button class="flex-1 py-3 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 focus:outline-none">
        SPK
      </button>
      <button class="flex-1 py-3 text-xs font-medium text-n-slate-10 hover:text-n-slate-12 focus:outline-none">
        Produk
      </button>
    </div>

    <!-- Empty State (Welcome Screen) -->
    <div class="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center">
      <div class="w-16 h-16 bg-n-alpha-1 rounded-full flex items-center justify-center mb-4">
        <span class="i-lucide-file-text text-n-brand size-8"></span>
      </div>
      <h4 class="text-base font-medium text-n-slate-12 mb-2">Belum ada data</h4>
      <p class="text-sm text-n-slate-10 mb-6">
        Pelanggan ini belum memiliki riwayat Invoice atau SPK. Buat baru atau sinkronkan data dari Cetakers Pro.
      </p>
      
      <div class="flex flex-col gap-2 w-full">
        <Button
          color="brand"
          class="w-full justify-center"
          icon="i-lucide-plus"
        >
          Buat Invoice Baru
        </Button>
        <Button
          ghost
          slate
          class="w-full justify-center"
          icon="i-lucide-refresh-cw"
        >
          Sinkronkan Data
        </Button>
      </div>
    </div>
  </div>
</template>
